import axios from 'axios';
import { getAgentsRequestQueryParams, getAgentsPayloadParams } from '@/utils/agentsApi';

/**
 * Creates handlers that translate AppBridge requests to Agents API calls.
 *
 * Since cx-portal uses the Agents API as an MCP proxy (no direct MCP client access),
 * we need to manually implement handlers that call the Agents API endpoints.
 *
 * @param {string} agentsUrl - Base URL for the Agents API
 * @param {string} accessToken - Bearer token for authentication
 * @param {string} cid - Client ID for the current client
 * @param {Object} [rootState] - Vuex root state (for region/cloud query params)
 * @returns {Object} Handler functions for AppBridge
 */
export function createAgentsApiHandlers(agentsUrl, accessToken, cid, rootState) {
  const headers = {
    'Authorization': `Bearer ${accessToken}`,
    'Content-Type': 'application/json'
  };
  const agentsPayload = () => getAgentsPayloadParams(rootState);

  return {
    /**
     * Read a resource from the MCP server via Agents API.
     * Maps to: POST /v1/aquabot/resource
     *
     * @param {Object} params - Request parameters
     * @param {string} params.uri - Resource URI to fetch
     * @returns {Promise<Object>} Resource contents in MCP format
     */
    async readResource(params) {
      const response = await axios.post(
        `${agentsUrl}/v1/aquabot/resource`,
        { uri: params.uri, cid, ...agentsPayload() },
        { headers, timeout: 60000 }
      );

      // Normalize response to MCP ReadResourceResult format
      const text = response.data.text || response.data.content ||
        (typeof response.data === 'string' ? response.data : JSON.stringify(response.data));

      return {
        contents: [{
          uri: params.uri,
          mimeType: 'text/html;profile=mcp-app',
          text
        }]
      };
    },

    /**
     * Call a tool on the MCP server via Agents API.
     * Maps to: POST /v1/aquabot/tool
     *
     * @param {Object} params - Request parameters
     * @param {string} params.name - Tool name
     * @param {Object} params.arguments - Tool arguments
     * @returns {Promise<Object>} Tool call result
     */
    async callTool(params) {
      const response = await axios.post(
        `${agentsUrl}/v1/aquabot/tool`,
        { name: params.name, arguments: params.arguments || {}, cid, ...agentsPayload() },
        { headers, timeout: 120000 }
      );

      return response.data;
    }
  };
}

/**
 * Fetch the list of available MCP tools with their metadata.
 * Tools with _meta.ui.resourceUri have associated UI visualizations.
 *
 * @param {string} agentsUrl - Base URL for the Agents API
 * @param {string} accessToken - Bearer token for authentication
 * @param {Object} [rootState] - Vuex root state (for region/cloud query params)
 * @returns {Promise<Object>} Object with tools array and toolsWithUi array
 */
export async function fetchMcpTools(agentsUrl, accessToken, rootState, cid) {
  const response = await axios.get(
    `${agentsUrl}/v1/aquabot/tools`,
    {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'X-Aqfer-Cid': cid
      },
      params: getAgentsRequestQueryParams(rootState),
      timeout: 30000
    }
  );

  return response.data;
}

/**
 * Extract UI resource URI from tool metadata.
 * Checks both nested format (_meta.ui.resourceUri) and flat format (_meta["ui/resourceUri"]).
 *
 * @param {Object} tool - Tool object with potential _meta property
 * @returns {string|null} UI resource URI or null if not present
 */
export function getToolUiResourceUri(tool) {
  if (!tool || !tool._meta) {
    return null;
  }

  // Try nested format first: _meta.ui.resourceUri
  if (tool._meta.ui && tool._meta.ui.resourceUri) {
    const uri = tool._meta.ui.resourceUri;
    if (typeof uri === 'string' && uri.startsWith('ui://')) {
      return uri;
    }
  }

  // Try flat format: _meta["ui/resourceUri"]
  const flatUri = tool._meta['ui/resourceUri'];
  if (typeof flatUri === 'string' && flatUri.startsWith('ui://')) {
    return flatUri;
  }

  return null;
}
